import React, { useMemo, useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Chip,
  useTheme,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import CustomTypography from "../common/form/CustomTypography";
import AppButton from "../common/buttons/AppButton";
import FormTextField from "../common/form/FormTextField";
import AppDropDown from "../common/filter/AppDropDown";
import PreviewHeader from "./PreviewHeader";
import { useStores } from "../../../use-stores";
import { observer } from "mobx-react-lite";
import { Swagpack } from "../../../models/Swagstore/Packs";
import { getPackDisplayImage } from "../../../utils/packImageUtils";

interface ProductsPageProps {
  deviceView: "desktop" | "tablet" | "mobile";
  hideHeader?: boolean;
  isStandalonePreview?: boolean;
}

const ProductsPage: React.FC<ProductsPageProps> = ({
  deviceView,
  hideHeader = false,
  isStandalonePreview = false,
}) => {
  const theme = useTheme();
  const { swagStoreStore } = useStores();
  const { branding } = swagStoreStore.settings;
  const { productsToDisplayOnProductPage } = swagStoreStore;

  const isLargeScreen = useMediaQuery("(min-width: 1600px)");
  const isMediumScreen = useMediaQuery(
    "(min-width: 1200px) and (max-width: 1400px)"
  );

  // Dummy products for preview when no products are available
  const dummyProducts: Swagpack[] = useMemo(
    () => [
      {
        id: -1,
        label: "Premium T-Shirt",
        pack_price: 599,
        pack_image_url:
          "https://content.swageazy.com/products/image-(101)_1749554515514.png",
        quantity: 0,
        available_quantity: 0,
        products_count: 0,
        used_quantity: 0,
        redeemQuantity: 0,
        category_name: "Apparel",
      },
      {
        id: -2,
        label: "Coffee Mug",
        pack_price: 299,
        pack_image_url:
          "https://content.swageazy.com/swageazyproducts/Insulated%2520Coffee%2520Mug_1730701251221.png",
        quantity: 0,
        available_quantity: 0,
        products_count: 0,
        used_quantity: 0,
        redeemQuantity: 0,
        category_name: "Drinkware",
      },
      {
        id: -3,
        label: "Portronics",
        pack_price: 1299,
        pack_image_url:
          "https://content.swageazy.com/products/61REtvdb7lL_1746522659834.png",
        quantity: 0,
        available_quantity: 0,
        products_count: 0,
        used_quantity: 0,
        redeemQuantity: 0,
        category_name: "Tech",
      },
      {
        id: -4,
        label: "T-Shirt",
        pack_price: 599,
        pack_image_url:
          "https://content.swageazy.com/swageazyproducts/Cotton-Polo-Neck-T-Shirt_1666784664718_1709888636855",
        quantity: 0,
        available_quantity: 0,
        products_count: 0,
        used_quantity: 0,
        redeemQuantity: 0,
        category_name: "Apparel",
      },
      {
        id: -5,
        label: "Borosil water Bottle",
        pack_price: 299,
        pack_image_url:
          "https://content.swageazy.com/swageazyproducts/41ZwJYmWVHL._SX679_-removebg-preview_1716371554041",
        quantity: 0,
        available_quantity: 0,
        products_count: 0,
        used_quantity: 0,
        redeemQuantity: 0,
        category_name: "Drinkware",
      },
      {
        id: -6,
        label: "Sticker",
        pack_price: 1299,
        pack_image_url:
          "https://content.swageazy.com/products/WD-sticker_1738759311757_1739532919068.PNG",
        quantity: 0,
        available_quantity: 0,
        products_count: 0,
        used_quantity: 0,
        redeemQuantity: 0,
        category_name: "Tech",
      },
    ],
    []
  );

  // In standalone preview mode, prioritize actual store products over dummy data
  // Only show dummy products if store has no products to display
  const displayProducts = useMemo(() => {
    // If we have products in the store, use them
    if (productsToDisplayOnProductPage && productsToDisplayOnProductPage.length > 0) {
      return productsToDisplayOnProductPage;
    }
    // Otherwise, show dummy products
    return dummyProducts;
  }, [productsToDisplayOnProductPage, dummyProducts]);

  const allCarouselImages = useMemo(() => {
    const DEFAULT_CAROUSEL_IMAGE =
      "https://developmentprocureasy.s3.ap-south-1.amazonaws.com/swagstore/40/new-banner.png";
    const images: string[] = [];

    // Add carousel images
    if (branding?.carouselImages && branding.carouselImages.length > 0) {
      branding.carouselImages.forEach((img) => {
        if (img instanceof File) {
          images.push(URL.createObjectURL(img));
        } else if (typeof img === "string") {
          images.push(img);
        }
      });
    }

    if (images.length === 0) {
      images.push(DEFAULT_CAROUSEL_IMAGE);
    }

    return images;
  }, [branding?.carouselImages]);

  useEffect(() => {
    return () => {
      allCarouselImages.forEach((url, index) => {
        // allCarouselImages.forEach((url, index) => {
        //   const isCarouselFile =
        //     branding?.carouselImages?.[index] instanceof File;

        //   if (isCarouselFile) {
        //     URL.revokeObjectURL(url);
        //   }
        // });

        const isCarouselFile =
          branding?.carouselImages?.[index] instanceof File;

        if (isCarouselFile) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [allCarouselImages, branding?.carouselImages]);

  const [carouselIndex, setCarouselIndex] = useState(0);

  const [selectedCategory, setSelectedCategory] = useState("All");

  useEffect(() => {
    if (allCarouselImages.length > 1) {
      const interval = setInterval(() => {
        setCarouselIndex((prev) => (prev + 1) % allCarouselImages.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [allCarouselImages.length]);

  const storeBackgroundColor =
    branding?.storeBackgroundColor || theme.palette.customColors.white;

  const productCardBackground =
    branding?.homepageProductCardBackground || theme.palette.customColors.white;

  const primaryButtonColor =
    branding?.primaryColor || theme.palette.customColors.purple;
  const secondaryButtonColor =
    branding?.secondaryColor || theme.palette.customColors.purple;

  const textFieldActiveColor =
    branding?.textFieldActiveColor || theme.palette.customColors.white;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        backgroundColor: storeBackgroundColor,
        pb: 0,
      }}
    >
      {!hideHeader && <PreviewHeader />}

      {/* Combined Carousel (Hero + Carousel Images) */}
      {allCarouselImages.length > 0 && (
        // Carousel images - with navigation
        <Box
          sx={{
            width: "100%",
            position: "relative",
            overflow: "hidden",
            height: isStandalonePreview
              ? deviceView === "mobile"
                ? "250px"
                : "450px"
              : deviceView === "mobile"
              ? "150px"
              : isLargeScreen
              ? "350px"
              : "250px",
            minHeight: isStandalonePreview
              ? deviceView === "mobile"
                ? "250px"
                : "450px"
              : deviceView === "mobile"
              ? "150px"
              : isLargeScreen
              ? "350px"
              : "250px",
            backgroundColor: theme.palette.customColors.veryLightGray,
          }}
        >
          {/* Carousel Images */}
          <Box
            sx={{
              display: "flex",
              width: `${allCarouselImages.length * 100}%`,
              transform: `translateX(-${
                carouselIndex * (100 / allCarouselImages.length)
              }%)`,
              transition: "transform 0.5s ease-in-out",
              height: "100%",
            }}
          >
            {allCarouselImages.map((img, index) => (
              <Box
                key={index}
                component="img"
                src={img}
                alt={index === 0 ? "Hero Banner" : `Carousel ${index}`}
                sx={{
                  width: `${100 / allCarouselImages.length}%`,
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "center",
                  flexShrink: 0,
                }}
              />
            ))}
          </Box>

          {/* Navigation Arrows - only show if more than 1 image */}
          {allCarouselImages.length > 1 && (
            <>
              <IconButton
                onClick={() =>
                  setCarouselIndex(
                    (prev) =>
                      (prev - 1 + allCarouselImages.length) %
                      allCarouselImages.length
                  )
                }
                sx={{
                  position: "absolute",
                  left: 7,
                  top: "50%",
                  transform: "translateY(-50%)",
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  color: theme.palette.customColors.blackText,
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                  },
                  zIndex: 1,
                }}
              >
                <ArrowBackIos sx={{ fontSize: "1rem", marginLeft: "3px" }} />
              </IconButton>
              <IconButton
                onClick={() =>
                  setCarouselIndex(
                    (prev) => (prev + 1) % allCarouselImages.length
                  )
                }
                sx={{
                  position: "absolute",
                  right: 7,
                  top: "50%",
                  transform: "translateY(-50%)",
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  color: theme.palette.customColors.blackText,
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                  },
                  zIndex: 1,
                }}
              >
                <ArrowForwardIos
                  sx={{ fontSize: "1rem", paddingLeft: "1px" }}
                />
              </IconButton>

              {/* Carousel Indicators */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: 8,
                  left: "50%",
                  transform: "translateX(-50%)",
                  display: "flex",
                  gap: 1,
                  zIndex: 1,
                }}
              >
                {allCarouselImages?.map((_, index) => (
                  <Box
                    key={index}
                    onClick={() => setCarouselIndex(index)}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor:
                        index === carouselIndex
                          ? theme.palette.customColors.white
                          : "rgba(255, 255, 255, 0.5)",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                    }}
                  />
                ))}
              </Box>
            </>
          )}
        </Box>
      )}

      {/* Products Section */}
      <Box
        sx={{
          p: deviceView === "mobile" ? 1.5 : isMediumScreen ? 2 : 3,
          width: "100%",
          boxSizing: "border-box",
          backgroundColor: storeBackgroundColor,
          marginBottom: branding?.footerText ? "0px" : "42px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: deviceView === "mobile" ? "column" : "row",
            justifyContent: "space-between",
            alignItems: deviceView === "mobile" ? "flex-start" : "center",
            mb: 2,
            gap: deviceView === "mobile" ? 2 : 0,
          }}
        >
          <CustomTypography
            variant="heading4"
            fontWeight={700}
            sx={{ color: theme.palette.customColors.blackText }}
          >
            {branding?.productHeadingText || "All Products"}
          </CustomTypography>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexWrap: "wrap",
              maxWidth: "100%",
              width: deviceView === "mobile" ? "100%" : "auto",
            }}
          >
            <Box
              sx={{
                minWidth: deviceView === "mobile" ? "100%" : "150px",
                flex: 1,
                maxWidth: deviceView === "mobile" ? "100%" : "200px",
              }}
            >
              <FormTextField
                placeholder="Search products..."
                value=""
                onChange={() => {}}
                sx={{
                  width: "100%",
                  marginBottom: "0px !important",
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: theme.palette.customColors.white,
                    borderRadius: "10px",
                    "&:hover fieldset": {
                      borderColor: textFieldActiveColor,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: textFieldActiveColor,
                    },
                  },
                }}
              />
            </Box>
            <Box
              sx={{
                minWidth: deviceView === "mobile" ? "100%" : "150px",
                flex: 1,
                maxWidth: deviceView === "mobile" ? "100%" : "180px",
              }}
            >
              <AppDropDown
                isMultiSelect={false}
                title="Price: Low to High"
                value=""
                list={[
                  { id: "low-high", value: "Price: Low to High" },
                  { id: "high-low", value: "Price: High to Low" },
                  { id: "newest", value: "Newest First" },
                  { id: "popular", value: "Most Popular" },
                ]}
                onSingleSelectionChange={() => {}}
                fontSize="0.8rem"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: theme.palette.customColors.white,
                    borderRadius: "10px",
                    "&.Mui-focused fieldset": {
                      borderColor: textFieldActiveColor,
                    },
                  },
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Category Filters */}
        <Box
          sx={{
            mb: 3,
            display: "flex",
            flexDirection: deviceView === "mobile" ? "column" : "row",
            alignItems: deviceView === "mobile" ? "flex-start" : "center",
            gap: deviceView === "mobile" ? 1 : 2,
            boxShadow: "rgba(205, 205, 205, 0.4) 0px 4px 6px",
            borderRadius: 2,
            padding: deviceView === "mobile" ? 1.5 : 2,
            mt: 3,
            backgroundColor: theme.palette.customColors.white,
          }}
        >
          <CustomTypography
            variant="heading6"
            fontWeight={600}
            color={theme.palette.customColors.greyTextColor}
            sx={{ mb: deviceView === "mobile" ? 0.5 : 1 }}
          >
            Categories:
          </CustomTypography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {["All", "Apparel", "Drinkware", "Tech"].map((category) => {
              const isSelected = selectedCategory === category;
              return (
                <Chip
                  key={category}
                  label={category}
                  onClick={() => setSelectedCategory(category)}
                  variant={isSelected ? "filled" : "outlined"}
                  sx={{
                    padding: "4px 10px",
                    minWidth: "60px",
                    backgroundColor: isSelected
                      ? swagStoreStore.settings.branding?.primaryColor
                      : "rgb(242, 242, 242)",
                    color: isSelected
                      ? theme.palette.customColors.white
                      : theme.palette.customColors.greyTextColor,
                    borderColor: isSelected
                      ? swagStoreStore.settings.branding?.primaryColor
                      : theme.palette.customColors.lightGray,
                    borderRadius: "20px",
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: isSelected
                        ? primaryButtonColor
                        : theme.palette.customColors.hoverGray,
                      borderColor: isSelected
                        ? primaryButtonColor
                        : theme.palette.customColors.lightGray,
                    },
                  }}
                />
              );
            })}
          </Box>
        </Box>

        {/* Product Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns:
              deviceView === "mobile"
                ? "1fr"
                : deviceView === "tablet"
                ? "repeat(2, 1fr)"
                : isLargeScreen
                ? "repeat(4, 1fr)"
                : isStandalonePreview
                ? "repeat(4, 1fr)"
                : isMediumScreen
                ? "repeat(2, 1fr)"
                : "repeat(3, 1fr)",
            gap: deviceView === "mobile" ? 1.5 : isMediumScreen ? 2 : 2,
            width: "100%",
            maxWidth: "100%",
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          {displayProducts?.map((product: Swagpack) => (
            <Card
              key={product.id}
              sx={{
                position: "relative",
                borderRadius: "12px",
                boxShadow: `0 2px 8px ${theme.palette.customColors.shadowMedium}`,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              <Box
                sx={{
                  height: "220px",
                  minHeight: "220px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  backgroundColor:
                    productCardBackground || theme.palette.customColors.white,
                  flexShrink: 0,
                }}
              >
                <CardMedia
                  component="img"
                  image={getPackDisplayImage(product)}
                  alt={product.label}
                  sx={{
                    objectFit: "contain",
                    maxWidth: "90%",
                    maxHeight: "90%",
                    width: "auto",
                    height: "auto",
                  }}
                />
              </Box>
              <CardContent
                sx={{
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  flexGrow: 1,
                  minHeight: 0,
                }}
              >
                <Box
                  sx={{
                    color: theme.palette.customColors.blackText,
                    fontSize: "1rem",
                    fontWeight: 600,
                    lineHeight: "1.25",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    marginBottom: "8px",
                    wordBreak: "break-word",
                    whiteSpace: "normal",
                    maxHeight: "2.5em", // lineHeight (1.25) * 2 lines = 2.5em
                    minHeight: "2.5em",
                  }}
                >
                  {product.label}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "16px",
                    flexShrink: 0,
                  }}
                >
                  <CustomTypography
                    variant="heading5"
                    fontWeight={700}
                    sx={{
                      color: theme.palette.customColors.blackText,
                    }}
                  >
                    ₹{product.pack_price?.toFixed(2) || "0"}
                  </CustomTypography>
                </Box>
                <Box
                  sx={{
                    marginTop: "auto",
                    flexShrink: 0,
                  }}
                >
                  <AppButton
                    label="Add to Cart"
                    onClick={() => {}}
                    variant="primaryBlock"
                    customStyles={{
                      fontSize: "0.75rem",
                      padding: "8px 16px",
                      backgroundColor: primaryButtonColor,
                      color: theme.palette.customColors.white,
                      fontWeight: 600,
                      width: "100%",
                      borderRadius: "15px",
                      "&:hover": {
                        backgroundColor: secondaryButtonColor,
                        color: theme.palette.customColors.white,
                      },
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>

      {/* Footer */}
      {branding?.footerText ? (
        <Box
          component="footer"
          sx={{
            width: "100%",
            minHeight: "60px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: storeBackgroundColor,
            borderTop: `1px solid ${theme.palette.divider}`,
            padding: "20px 16px",
            marginTop: "auto",
            flexShrink: 0,
            marginBottom: "42px",
            zIndex: 1,
          }}
        >
          <CustomTypography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              textAlign: "center",
              margin: 0,
              lineHeight: 1.5,
              width: "100%",
            }}
          >
            {branding?.footerText ||
              "© Swageazy 2025. All rights reserved. v1.0.7"}
          </CustomTypography>
        </Box>
      ) : null}
    </Box>
  );
};

export default observer(ProductsPage);
